import React, { useCallback, useEffect, useRef, useState } from "react";
import clsx from "clsx";
import { LinearProgress, Paper, Portal, Slide, useOnClickOutside } from "@equinor/opt-ui-core";
import { Actions, Container, Content } from "./styles";
// Component
export const NotificationApi = function NotificationApi({ ref, className, children, container = undefined, disablePortal = false, open: stateOpen, message = undefined, actions = undefined, autoHide = true, autoHideDuration = 6000, showAutoHideLoader = true, closeOnClickOutside = false, placement = "bottom-start", onClose = undefined, ...other }) {
    const [open, setOpen] = useState(stateOpen);
    const containerRef = useRef(null);
    const timerAutoHide = useRef(null);
    const timerTimeElapsed = useRef(null);
    const [timeElapsed, setTimeElapsed] = useState(0);
    const hasChildren = typeof children !== 'undefined' && !!children;
    const timeToAdd = Math.min(Math.floor(100 / (autoHideDuration / 1000)), 100);
    // Handle close/resume
    const handleClose = () => {
        if (autoHide && showAutoHideLoader)
            setTimeElapsed(0);
        if (autoHide)
            setOpen(false);
        if (onClose)
            onClose();
    };
    const setAutoHideTimer = (duration = autoHideDuration) => {
        if (!onClose && !autoHide)
            return;
        clearTimeout(timerAutoHide.current);
        timerAutoHide.current = setTimeout(() => {
            handleClose();
        }, duration);
        if (showAutoHideLoader) {
            clearInterval(timerTimeElapsed.current);
            timerTimeElapsed.current = setInterval(() => {
                if (containerRef.current) {
                    const now = containerRef.current.childNodes[0].getAttribute('aria-valuenow');
                    setTimeElapsed(+now + timeToAdd);
                }
            }, 500);
        }
    };
    const handleResume = useCallback(() => {
        if (autoHide) {
            setAutoHideTimer(autoHideDuration * 0.5);
        }
    }, [autoHide]);
    const handlePause = () => {
        clearTimeout(timerAutoHide.current);
        clearInterval(timerTimeElapsed.current);
    };
    // Handle events
    const handleMouseEnter = () => {
        handlePause();
    };
    const handleMouseLeave = () => {
        handleResume();
    };
    // Sync open state
    useEffect(() => setOpen(stateOpen), [stateOpen]);
    // Click outside
    if (closeOnClickOutside) {
        useOnClickOutside(containerRef, () => {
            if (onClose)
                handleClose();
        });
    }
    // Resume or pause notification timer if the window is idle or active
    useEffect(() => {
        if (open) {
            window.addEventListener('focus', handleResume);
            window.addEventListener('blur', handlePause);
            return () => {
                window.removeEventListener('focus', handleResume);
                window.removeEventListener('blur', handlePause);
            };
        }
        return undefined;
    }, [open]);
    // Create timer to hide notification
    useEffect(() => {
        if (open) {
            setAutoHideTimer(autoHideDuration);
            if (autoHide && showAutoHideLoader)
                setTimeElapsed(0);
        }
        return () => {
            clearTimeout(timerAutoHide.current);
            clearInterval(timerTimeElapsed.current);
        };
    }, [open]);
    // Methods
    const getSlideDirection = () => {
        if (placement === 'top')
            return 'down';
        if (placement === 'bottom')
            return 'up';
        if (placement.includes('start'))
            return 'right';
        return 'left';
    };
    const getPlacement = () => {
        const p = {};
        if (placement.includes('top'))
            p.top = '16px';
        if (placement.includes('bottom'))
            p.bottom = '16px';
        if (placement.includes('start'))
            p.left = '16px';
        if (placement.includes('end'))
            p.right = '16px';
        if (!placement.includes('-')) {
            p.margin = '0 auto';
            p.left = 0;
            p.right = 0;
        }
        return p;
    };
    // Render
    return (React.createElement(Portal, { ref: ref, container: container, disablePortal: disablePortal },
        React.createElement(Slide, { in: open, mountOnEnter: true, unmountOnExit: true, direction: getSlideDirection() },
            React.createElement(Paper, { ref: containerRef, className: clsx(className, 'opt-notification'), onMouseEnter: handleMouseEnter, onMouseLeave: handleMouseLeave, position: "fixed", ...getPlacement(), minWidth: "256px", bg: !hasChildren ? '#000000cc' : null, color: !hasChildren ? '#FFF' : null, fontSize: "0.75rem", fontWeight: "500", ...other },
                autoHide && showAutoHideLoader && (React.createElement(LinearProgress, { variant: "determinate", value: timeElapsed > 100 ? 100 : timeElapsed })),
                React.createElement(Container, null,
                    hasChildren && (React.createElement(Content, { p: 0, width: "100%" }, children)),
                    !hasChildren && (React.createElement(React.Fragment, null,
                        React.createElement(Content, null, message),
                        actions && React.createElement(Actions, null, actions))))))));
};
export default NotificationApi;
